import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dimmer, Input, Loader, Header, Form } from 'semantic-ui-react';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import uuid from 'uuid/v1';
import { Redirect } from 'react-router-dom';
import {
  getUUID,
  getAltAccountNumber,
  isCCPaymentsAllowed,
  isECheckPaymentsAllowed,
  isHideContributionOptionForRenew,
  isValidAllEmailAddress,
  isValidPhoneNumber,
} from '../../containers/Utils';
import TermSelector from '../Terms';
import { USTerms, InternationalTerms } from '../Terms/terms';
import ContributionSelector from '../Contribution';
import DateOfBirth from '../DateOfBirth';
import StateSelector from '../States';
import CountrySelector from '../Countries';
import PaymentMethodSelector from '../PaymentMethods';

import './styles.css';

let paymentSessionId;

class Renew extends React.Component {
  static propTypes = {
    memberRequest: PropTypes.object,
    renewMember: PropTypes.func,
    paymentResponse: PropTypes.object,
    setPaymentRequest: PropTypes.func.isRequired,
    removePaymentResponse: PropTypes.func.isRequired,
    setMemberRequest: PropTypes.func.isRequired,
    removeMemberRequest: PropTypes.func.isRequired,
    setSuccessMessage: PropTypes.func.isRequired,
    setErrorMessage: PropTypes.func.isRequired,
    validateAddress: PropTypes.func.isRequired,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const newState = {};
    if (!isEmpty(nextProps.paymentResponse) && !prevState.renewActive) {
      newState.renewAllowSubmit = true;
      newState.paymentActive = false;
    } else if (isEmpty(nextProps.paymentRequest) && prevState.paymentActive) {
      newState.paymentActive = false;
    }
    if (!isEmpty(newState)) {
      return newState;
    }
    return null;
  }

  constructor(props) {
    super(props);
    paymentSessionId = getUUID();
    if (isEmpty(props.memberRequest)) {
      this.state = {
        cancel: true,
      };
    } else {
      const member = omit(props.memberRequest, ['dateOfBirth', 'spouseDateOfBirth']);
      member.contributionAmount = '';
      this.state = {
        member,
      };
      props.removeMemberRequest();
    }
    this.state.member.paymentMethod = 'creditCard';
  }

  componentDidUpdate() {
    if (this.state.renewAllowSubmit) {
      this.submit(this.props.paymentResponse);
    }
  }

  componentWillUnmount() {
    // istanbul ignore else
    if (!isEmpty(this.props.paymentResponse)) {
      this.props.removePaymentResponse();
    }
  }

  onChangeValue = (_, data) => {
    const { member } = this.state;
    member[data.name] = data.value;
    this.setState({
      member,
    });
  }

  onKeyPressExecute = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  onChangeTermsValue = (_, data) => {
    const member = { ...this.state.member };
    member[data.name] = data.value;
    this.setState({
      member,
    });
    if (data.value === 'US12') {
      member.paymentMethod = 'creditCard';
      this.state.member.paymentMethod = 'creditCard';
    }
  }

  onChangeCountryValue = (_, data) => {
    // create a copy to not mutate state.
    const member = { ...this.state.member };
    if (member.country !== data.value) {
      member.membershipTerm = '';
    }
    member.country = data.value;
    this.setState({
      member,
    });
  }

  onChangePaymentMethodValue = (_, data) => {
    const member = { ...this.state.member };
    member[data.name] = data.value;
    this.setState({
      member,
    });
  }

  getTerm = (term) => {
    let membershipTerm = USTerms.filter(usTerm => usTerm.id === term);
    if (membershipTerm.length === 0) {
      membershipTerm = InternationalTerms.filter(intlTerm => intlTerm.id === term);
    }

    if (membershipTerm.length === 1) {
      return membershipTerm[0];
    }
    return {};
  }

  handleCancel = () => {
    if (!isEmpty(this.props.paymentResponse)) {
      this.props.removePaymentResponse();
    }
    this.setState({ cancel: true });
  }

  isValidForRenew = () => {
    const { member } = this.state;
    return !isEmpty(member.lastName) &&
      !isEmpty(member.firstName) &&
      !isEmpty(member.membershipTerm) &&
      !isEmpty(member.addressLine1) &&
      !isEmpty(member.city) &&
      !isEmpty(member.state) &&
      !isEmpty(member.zipCode) &&
      !isEmpty(member.country) &&
      !isEmpty(member.paymentMethod);
  };

  mapPayment = (member, paymentResponse) => {
    if (paymentResponse) {
      const response = { ...paymentResponse.response };

      const today = new Date();
      let month = (1 + today.getMonth()).toString();
      month = month.length > 1 ? month : `0${month}`;

      let day = today.getDate().toString();
      day = day.length > 1 ? day : `0${day}`;

      const todayDate = `${month}/${day}/${today.getFullYear()}`;

      let ccAuth = {};
      let eCheckAuth = {};
      member.paymentMethod = (member.paymentMethod === undefined ? 'creditCard' : member.paymentMethod);
      if (member.paymentMethod === 'creditCard') {
        ccAuth = {
          firstName: response.req_bill_to_forename,
          lastName: response.req_bill_to_surname,
          addrLine1: response.req_bill_to_address_line1,
          addrLine2: response.req_bill_to_address_line2 || '',
          city: response.req_bill_to_address_city,
          state: response.req_bill_to_address_state,
          zip: response.req_bill_to_address_postal_code,
          countryCode: response.req_bill_to_address_country,
          phone: response.req_bill_to_phone,
          cardExpireDt: response.req_card_expiry_date ? response.req_card_expiry_date.replace('-', '') : '',
          cardType: response.req_card_type,
          cclast4Digits: response.req_card_number ? response.req_card_number.slice(-4) : '',
          subscriptionId: response.payment_token,
          currencyCd: 'USD',
          amount: response.req_amount,
          requestId: response.transaction_id,
          merchantId: process.env.MERCHANT_ID,
          merchantReferenceCd: response.merchant_reference_code,
          cvv2: 'NOT_REQUIRED',
        };
      } else if (member.paymentMethod === 'eCheck') {
        eCheckAuth = {
          firstName: response.req_bill_to_forename,
          lastName: response.req_bill_to_surname,
          addrLine1: response.req_bill_to_address_line1,
          addrLine2: response.req_bill_to_address_line2 || '',
          city: response.req_bill_to_address_city,
          state: response.req_bill_to_address_state,
          zip: response.req_bill_to_address_postal_code,
          countryCode: response.req_bill_to_address_country,
          merchantId: process.env.MERCHANT_ID,
          subscriptionId: response.subscriptionId,
          currencyCd: 'USD',
          amount: response.req_amount,
          echeckAuthInd: 'Y',
          acctLast4Digits: response.req_echeck_account_number,
        };
      }

      const payment = {
        accountStatus: '0',
        assocInd: member.accountIndicator,
        ccAuthorization: ccAuth,
        wsecDebit: eCheckAuth,
        decisionManager: {
          merchantFld1: 'web',
          merchantFld5: 'individual',
          deviceFingerprint: paymentSessionId,
          ipAddress: window.localStorage.getItem('publicIP'),
        },
        depositDate: todayDate,
        paymentAmount: member.membershipTerm,
        contributionAmount: member.contributionAmount,
        primaryPersonId: member.personId,
        accountId: member.id,
        expireDate: member.expiryDate,
        accountType: member.accountType,
      };

      if (member.paymentMethod === 'creditCard') {
        payment.paymentMethod = 'R';
      } else if (member.paymentMethod === 'eCheck') {
        payment.paymentMethod = 'A';
      }
      return payment;
    }
    return null;
  }

  isValidateInputs = () => {
    const { setErrorMessage } = this.props;
    const { member } = this.state;
    let errorMessage = '';
    errorMessage += isValidPhoneNumber(member.phoneNumber);
    errorMessage += isValidAllEmailAddress(member.emailAddress, '');
    if (errorMessage !== '') {
      setErrorMessage(errorMessage.substring(0, errorMessage.length - 2));
      return false;
    }
    return true;
  }

  handleRenew = (event) => {
    if (this.isValidateInputs()) {
      const { validateAddress, setErrorMessage } = this.props;
      const { member, errorRenew } = this.state;
      if (errorRenew) {
        this.submit(this.props.paymentResponse);
      } else {
        const address = {
          addressLine1: member.addressLine1,
          addressLine2: member.addressLine2,
          city: member.city,
          state: member.state,
          zipCode: member.zipCode,
          country: member.country,
        };
        this.setState({ validateAddressActive: true });

        const p = (member.country === 'US' || member.country === '') ? validateAddress(address) : Promise.resolve({});
        p.then((response) => {
          this.merchantReferenceCode = uuid();
          this.props.setPaymentRequest({
            member: {
              ...member,
              phoneNumber: !member.phoneNumber || member.phoneNumber === '' ? '1231231234' : member.phoneNumber,
            },
            merchantReferenceCode: this.merchantReferenceCode,
            amount: this.getTerm(member.membershipTerm).value + Number(member.contributionAmount),
          });
          this.setState({ validateAddressActive: false, paymentActive: true });
        })
          .catch((err) => {
            this.setState({ validateAddressActive: false });
            setErrorMessage(err);
          });
      }
    }
  }

  submit = (paymentResponse) => {
    const {
      renewMember,
      setMemberRequest,
      setSuccessMessage,
      setErrorMessage,
      removePaymentResponse,
    } = this.props;

    const { member } = this.state;
    const term = this.getTerm(member.membershipTerm);
    member.membershipTerm = term.value;
    member.autoRenew = term.autoRenew ? 'Y' : 'N';

    if (paymentResponse.response.error) {
      this.setState({ renewActive: false, renewAllowSubmit: false });
      setErrorMessage(paymentResponse.response.message);
      this.props.removePaymentResponse();
      return null;
    }

    const payment = this.mapPayment(member, paymentResponse);
    member.payment = payment;

    this.setState({
      renewAllowSubmit: false,
      renewActive: true,
    });

    return renewMember({
      variables: {
        member: omit(member, ['__typename', 'expiryDate', 'contributionAmount', 'paymentMethod']),
      },
    })
      .then((response) => {
        setMemberRequest({ ...response.data.renewMember });
        this.setState({
          member: response.data.renewMember,
          errorRenew: false,
          renewActive: false,
          renewSuccess: true,
        });
        removePaymentResponse();
        setSuccessMessage('Member renewed.');
      })
      .catch((err) => {
        this.setState({ renewActive: false, errorRenew: true });
        setErrorMessage(`Failed to Renew due to error: ${err.message.replace('GraphQL error: ', '')}`);
      });
  }

  render() {
    const {
      memberLoading,
      member,
      cancel,
      renewActive,
      renewSuccess,
      renewAllowSubmit,
      validateAddressActive,
      paymentActive,
    } = this.state;

    if (cancel) {
      return <Redirect to="/search" />;
    }

    if (renewSuccess) {
      return <Redirect to="/view" />;
    }

    let altAcctNumber = '';
    if (member.id !== undefined) {
      altAcctNumber = getAltAccountNumber(this.props, member.id);
    }

    const submitEnabled = this.isValidForRenew();
    let loadingMessage = renewActive && 'Renewing Member...';
    loadingMessage = loadingMessage || (paymentActive && 'Invoking Payment...');
    loadingMessage = loadingMessage || (validateAddressActive && 'Validating Address...');

    return (
      <React.Fragment>
        {
          memberLoading &&
          <Dimmer.Dimmable as="div" styleName="renew-container">
            <Dimmer active={memberLoading}>
              <Loader inverted>Loading Member...</Loader>
            </Dimmer>
          </Dimmer.Dimmable>
        }
        {
          !memberLoading &&
          <Dimmer.Dimmable
            as="div"
            styleName="renew-container"
            dimmed={renewActive || renewAllowSubmit || paymentActive || validateAddressActive}
          >
            <Dimmer active={renewActive || renewAllowSubmit || paymentActive || validateAddressActive}>
              <Loader inverted>{loadingMessage}</Loader>
            </Dimmer>
            <Header as="h2" dividing styleName="renew-header">
              <Header.Content>
                Renewal
                {
                  member.expiryDate &&
                  <Header.Subheader>
                    Your account is currently paid through {member.expiryDate}
                  </Header.Subheader>
                }
                {
                  member.autoRenew === 'Y' &&
                  <Header.Subheader>
                    Member is currently using Auto Renew
                  </Header.Subheader>
                }
                {
                  member.autoRenew !== 'Y' &&
                  <Header.Subheader>
                    Member is currently not using Auto Renew
                  </Header.Subheader>
                }
              </Header.Content>
            </Header>
            <Form>
              <TermSelector
                member={member}
                fieldChangeHandler={this.onChangeTermsValue}
                allowCCPayments={isCCPaymentsAllowed(this.props)}
                allowECheckPayments={isECheckPaymentsAllowed(this.props)}
                subHeader="Please select a renewal term below:"
              />
              <div styleName="member-info">
                <Header as="h3" dividing>
                  <Header.Content>
                    Member Information
                    <Header.Subheader>
                      Please review the following information and change if necessary.
                    </Header.Subheader>
                  </Header.Content>
                </Header>
                <Form.Group>
                  <Form.Field
                    control={Input}
                    label="Membership Number"
                    value={member.id}
                    name="membershipNumber"
                    readOnly
                  />
                  <Form.Field
                    control={Input}
                    label="Membership Type"
                    value={member.accountIndicator === 'N' ? 'NRTA' : 'AARP'}
                    name="accountIndicator"
                    readOnly
                    key="accountIndicator"
                    styleName="member-type"
                  />
                </Form.Group>
                <div>
                  {
                    altAcctNumber !== '' ?
                      <Form.Group>
                        <Form.Field label="For AARP Medicare Supplement Insurance Applications, use this Number" control={Input}>
                          <Form.Field
                            control={Input}
                            value={altAcctNumber}
                            name="altAccountNumber"
                            readOnly
                          />
                        </Form.Field>
                      </Form.Group>
                      : null
                  }
                </div>
                <Form.Group widths="equal">
                  <Form.Field
                    control={Input}
                    label="First Name"
                    value={member.firstName}
                    onChange={this.onChangeValue}
                    name="firstName"
                    required
                    fluid
                    autoComplete="disabled"
                  />
                  <Form.Field
                    control={Input}
                    label="Middle Name"
                    value={member.middleName}
                    onChange={this.onChangeValue}
                    name="middleName"
                    fluid
                    autoComplete="disabled"
                  />
                  <Form.Field
                    control={Input}
                    label="Last Name"
                    value={member.lastName}
                    onChange={this.onChangeValue}
                    name="lastName"
                    required
                    fluid
                    autoComplete="disabled"
                  />
                </Form.Group>
                <DateOfBirth
                  member={member}
                  fieldChangeHandler={this.onChangeValue}
                  memberLevel="primary"
                />
                <Form.Group widths="equal">
                  <Form.Field
                    control={Input}
                    label="Mailing Address"
                    value={member.addressLine1}
                    onChange={this.onChangeValue}
                    name="addressLine1"
                    required
                    fluid
                    autoComplete="disabled"
                  />
                  <Form.Field
                    control={Input}
                    label="Address (line 2)"
                    value={member.addressLine2}
                    onChange={this.onChangeValue}
                    name="addressLine2"
                    fluid
                    autoComplete="disabled"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Field
                    control={Input}
                    label="City"
                    value={member.city}
                    onChange={this.onChangeValue}
                    name="city"
                    width="8"
                    required
                    autoComplete="disabled"
                  />
                  <StateSelector
                    member={member}
                    fieldChangeHandler={this.onChangeValue}
                    fieldkey="stateField"
                    width="5"
                    required
                  />
                  <Form.Field
                    control={Input}
                    label="Zip/Postal Code"
                    value={member.zipCode}
                    onChange={this.onChangeValue}
                    name="zipCode"
                    width="3"
                    required
                    autoComplete="disabled"
                  />
                </Form.Group>
                <CountrySelector
                  member={member}
                  fieldChangeHandler={this.onChangeCountryValue}
                  width="4"
                  required
                />
                <Form.Field
                  control={Input}
                  label="Phone"
                  value={member.phoneNumber}
                  onChange={this.onChangeValue}
                  name="phoneNumber"
                  placeholder="5555555555"
                  width="4"
                  autoComplete="disabled"
                  maxLength="10"
                  onKeyPress={this.onKeyPressExecute}
                />
                <Form.Field
                  control={Input}
                  label="Email Address"
                  value={member.emailAddress}
                  onChange={this.onChangeValue}
                  name="emailAddress"
                  width="12"
                  autoComplete="disabled"
                />
                <div>
                  {
                    (!isHideContributionOptionForRenew(this.props)) ?
                      <ContributionSelector
                        padTop
                        member={member}
                        fieldChangeHandler={this.onChangeValue}
                      />
                      : null
                  }
                </div>
                <br />
                <div>
                  {
                    (isECheckPaymentsAllowed(this.props)) ?
                      <PaymentMethodSelector
                        member={member}
                        fieldChangeHandler={this.onChangePaymentMethodValue}
                        allowCCPayments={isCCPaymentsAllowed(this.props)}
                        allowECheckPayments={isECheckPaymentsAllowed(this.props)}
                      />
                      : null
                  }
                </div>
                <Button
                  id="payWithPopupButton"
                  styleName="renew-button"
                  disabled={!submitEnabled}
                  type="button"
                  onClick={this.handleRenew}
                  content="Submit"
                />
                <Button type="button" styleName="cancel-button" onClick={this.handleCancel}>Cancel</Button>
                <img
                  // eslint-disable-next-line
                  src={`https://h.online-metrix.net/fp/clear.png?org_id=${process.env.CYBERSOURCE_ORGID}&session_id=${process.env.MERCHANT_ID}${paymentSessionId}&m=2`}
                  alt=""
                />
              </div>
            </Form>
          </Dimmer.Dimmable>
        }
      </React.Fragment>
    );
  }
}

export default Renew;



// WEBPACK FOOTER //
// ./src/app/components/Renew/index.js